import { ActionTree } from 'vuex';
import { IReplaceTempBlock } from '@/dealroom/types/replace-temp-block';
import { IProgress } from '@/dealroom/add-page/import-progress/types/progress';
import { EImportProgressAction } from '@/dealroom/add-page/import-progress/store/import-progress.constants';
import { IImportProgressState } from '@/dealroom/add-page/import-progress/store/import-progress.types';
import { IUpdateDocumentPageInput } from '@/dealroom/types/update-document-page-input';
import {
  getterConnectFirstRecipient,
  getterFirstAddedRecipient,
} from '@/dealroom/store/dealroom.getters';
import { EImportStep } from '@/dealroom/add-page/import-progress/types/enums/import-step';
import { EImportType } from '@/dealroom/add-page/import-progress/types/enums/import-type';
import { IWordMergeField } from '@/dealroom/integrations/word/types/word-merge-field';
import { DocumentService } from '@/api/documents/services/document.service';
import GoogleService from '@/api/integrations/google/google.service';
import { IContentLinkBlock } from '@/dealroom/content-link/types/content-link';
import { IUpdateDocument } from '@/dealroom/document/types/update-document';
import { EGoogleDriveError } from '@/dealroom/integrations/google/drive/types/google-drive-error';
import { IUploadData } from '@/dealroom/integrations/google/types/upload-data';
import { DealroomHelper } from '@/dealroom/helpers/dealroom.helper';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import service from '@/dealroom/services/dealroom.service';
import {
  mutationSetContentLink,
  mutationAddDocumentTag,
  mutationAddRecipient,
  mutationShowEditDocumentPage,
  mutationRemoveDocumentTag,
  mutationSetCurrentDocumentPage,
  mutationSetDocument,
  mutationSetDocumentPages,
  mutationSetLoaded,
  mutationSetLoading,
  mutationSetRecipients,
  mutationSetSelectedRecipient,
  mutationSetSelectedUser,
  mutationSetState,
  mutationUpdateDocumentAttachment,
  mutationUpdateDocumentUser,
  mutationSetWordMerge,
  mutationSetPreviewToken,
  mutationUpdateRecipient,
  mutationShowDocumentSettings,
  mutationSetSealValidationSuccess,
  mutationSetPageLoading,
  mutationReplaceTempBlock,
  mutationRemoveTempBlock,
  mutationSetCreateDocumentId,
  mutationSetSidebarCollapsed,
} from '@/dealroom/store/dealroom.mutations';
import { IDealroomState, state as defaultState } from '@/dealroom/store/dealroom.store';
import { IDocumentImportData } from '@/dealroom/types/document-import-data';
import { IWordMerge } from '@/dealroom/types/word-merge';
import WordService from '@/dealroom/integrations/word/services/word.service';
import { IProcessWordResponse } from '@/dealroom/integrations/word/types/process-word-response';
import { IWordTemplateResponse } from '@/dealroom/integrations/word/types/word-template-response';
import { IWordUploadResponse } from '@/dealroom/integrations/word/types/word-upload-response';
import { EDocumentPageType } from '@/enums/document-page-type';
import { EExternalEditorType } from '@/enums/ExternalEditorType';
import {
  actionLoadFields,
  actionLoadSenderSignature,
  actionUpdateFields,
  actionDeleteSignatureFields,
} from '@/fields/store/field.actions';
import { namespace as fieldNamespace } from '@/fields/store/field.store';
import { EFieldSource } from '@/fields/types/enums/field-source';
import { IField } from '@/fields/types/field';
import { translate } from '@/helpers/translation.helper';
import { arrangeRecipientSignOrder, fixRecipientsGaps } from '@/recipients/recipient.helper';
import {
  actionInitializeDocumentEdit,
  actionLoadDocumentData,
} from '@/store/actions/document-edit.actions';
import { RootState } from '@/store/store';
import { namespace as documentEditNamespace } from '@/store/stores/document-edit.store';
import tagService from '@/tags/services/tag.service';
import { IDocument } from '@/types/Document';
import { IDocumentAttachment } from '@/types/document-attachment';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { ITag } from '@/types/Tag';
import { IUser } from '@/types/User';
import { EnumDocumentType } from '@/enums/DocumentType';
import { ICreateEditorBlockPayload } from '@/editor/types';
import { ContentLinkBlockService } from '@/api/content-link/services/content-link-block.service';
import { EditorHelper } from '@/editor/helpers/editor.helper';

export const actionLoadDocument = 'loadDocument';
export const actionLoadDocumentSuccess = 'loadDocumentSuccess';
export const actionLoadDocumentFailed = 'loadDocumentFailed';

export const actionShowEditDocumentPage = 'showEditDocumentPage';

export const actionCreateContentLinkDocumentPage = 'createContentLinkDocumentPage';
export const actionCreateVideoDocumentPage = 'createVideoDocumentPage';

export const actionCreateEditorBlock = 'createEditorBlock';
export const actionCreateEditorBlockSuccess = 'createEditorBlockSuccess';
export const actionCreateEditorBlockFailed = 'createEditorBlockFailed';

export const actionCreateDocumentPageSuccess = 'createDocumentPageSuccess';
export const actionCreateDocumentPageFailed = 'createDocumentPageFailed';

export const actionDeleteDocumentPage = 'deleteDocumentPage';
export const actionDeleteDocumentPageFailed = 'deleteDocumentPageFailed';

export const actionCreateContentLinkBlock = 'createContentLink';
export const actionCreateContentLinkBlockFailed = 'createContentLinkFailed';

export const actionUpdateContentLinkPage = 'updateContentLinkPage';
export const actionUpdateContentLinkPageFailed = 'updateContentLinkPageFailed';

export const actionUpdateVideoPage = 'updateVideoPage';
export const actionUpdateVideoPageFailed = 'updateVideoPageFailed';

export const actionUploadFile = 'uploadFile';
export const actionUploadFileSuccess = 'uploadFileSuccess';
export const actionUploadFileFailed = 'uploadFileFailed';

export const actionImportPages = 'importPages';

export const actionResetState = 'resetState';
export const actionShowDocumentSettings = 'showDocumentSettings';

export const actionLoadDocumentPage = 'loadDocumentPage';
export const actionLoadDocumentPageSuccess = 'loadDocumentPageSuccess';
export const actionLoadDocumentPageFailed = 'loadDocumentPageFailed';

export const actionReplaceDocumentPage = 'replaceDocumentPage';

export const actionUpdateDocument = 'updateDocument';
export const actionUpdateDocumentFailed = 'updateDocumentFailed';

export const actionAddDocumentTag = 'addDocumentTag';
export const actionAddDocumentTagSuccess = 'addDocumentTagSuccess';
export const actionAddDocumentTagFailed = 'addDocumentTagFailed';

export const actionRestrictAccessAttachment = 'restrictAccessAttachment';
export const actionRestrictAccessAttachmentFailed = 'restrictAccessAttachmentFailed';

export const actionRemoveRestrictedAccessAttachment = 'removeRestrictedAccessAttachment';
export const actionRemoveRestrictedAccessAttachmentFailed =
  'removeRestrictedAccessAttachmentFailed';

export const actionRequireViewAttachment = 'requireViewAttachment';
export const actionRequireViewAttachmentFailed = 'requireViewAttachmentFailed';

export const actionDeleteAttachment = 'deleteAttachment';
export const actionDeleteAttachmentFailed = 'deleteAttachmentFailed';

export const actionRemoveDocumentTag = 'removeDocumentTag';
export const actionRemoveDocumentTagFailed = 'removeDocumentTagFailed';

export const actionSetCurrentDocumentPage = 'setCurrentDocumentPage';

export const actionSetSelectedUser = 'setSelectedUser';
export const actionSetSelectedRecipient = 'setSelectedRecipient';

export const actionRemoveRecipient = 'removeRecipient';
export const actionRemoveRecipientSuccess = 'removeRecipientSuccess';
export const actionRemoveRecipientFailed = 'removeRecipientFailed';

export const actionCreateRecipient = 'createRecipient';
export const actionCreateRecipientSuccess = 'createRecipientSuccess';
export const actionCreateRecipientFailed = 'createRecipientFailed';

export const actionUpdateRecipient = 'updateRecipient';
export const actionUpdateRecipientFailed = 'updateRecipientFailed';
export const actionTurnOnSigningOrder = 'turnOnSigningOrder';
export const actionTurnOffSigningOrder = 'turnOffSigningOrder';
export const actionUpdateRecipientApproversToCC = 'updateRecipientApproversToCC';
export const actionLoadDocumentAttachments = 'loadDocumentAttachments';
export const actionLoadDocumentAttachmentsSuccess = 'loadDocumentAttachmentsSuccess';
export const actionLoadDocumentAttachmentsFailed = 'loadDocumentAttachmentsFailed';

export const actionUpdateRecipientsSigningOrder = 'updateRecipientsSigningOrder';
export const actionUpdateRecipientsSigningOrderFailed = 'updateRecipientsSigningOrderFailed';

export const actionLoadRecipients = 'loadRecipients';
export const actionLoadRecipientsSuccess = 'loadRecipientsSuccess';
export const actionLoadRecipientsFailed = 'loadRecipientsFailed';

export const actionUseTemplate = 'useTemplate';
export const actionUseTemplateSuccess = 'useTemplateSuccess';
export const actionUseTemplateFailed = 'useTemplateFailed';
export const actionLoadLegacyDocumentData = 'loadLegacyDocumentData';

export const actionDeleteDocuments = 'deleteDocuments';
export const actionDeleteDocumentsSuccess = 'deleteDocumentsSuccess';
export const actionDeleteDocumentsFailed = 'deleteDocumentsFailed';

export const actionDeleteTemplate = 'deleteTemplate';
export const actionDeleteTemplateFailed = 'deleteTemplateFailed';

export const actionUpdateDocumentUser = 'updateDocumentUser';
export const actionUpdateDocumentUserFailed = 'updateDocumentUserFailed';
export const actionArrangeDocumentPages = 'arrangeDocumentPages';
export const actionArrangeDocumentPagesFailed = 'arrangeDocumentPagesFailed';

export const actionSaveGoogleDrive = 'saveGoogleDrive';
export const actionSaveGoogleDriveFailed = 'saveGoogleDriveFailed';
export const actionHandleGoogleDriveError = 'handleGoogleDriveError';

export const actionInitializePageImport = 'initializePageImport';

export const actionSaveGoogleDocs = 'saveGoogleDocs';

export const actionSortSigningRecipients = 'sortSigningRecipients';

export const actionUploadWordFile = 'uploadWordFile';
export const actionProcessWordFile = 'processWordFile';
export const actionSetWordMerge = 'setWordMerge';
export const actionCancelWordMerge = 'cancelWordMerge';
export const actionSaveWordMergeTemplate = 'saveWordMergeTemplate';

export const actionConnectFirstRecipientToFields = 'connectFirstRecipientToFields';

export const actionGetPreviewToken = 'getPreviewToken';
export const actionSetDocumentPages = 'setDocumentPages';

export const actionUpdateRecipientMobile: string = 'updateRecipientMobile';
export const actionUpdateRecipientMobileSuccess: string = 'updateRecipientMobileSuccess';

export const actionloadPageThumbUrl: string = 'loadPageThumbUrl';

export const actionCreateDocument: string = 'createDocument';
export const actionCreateDocumentSuccess: string = 'createDocumentSuccess';
export const actionCreateDocumentFailed: string = 'createDocumentFailed';

export const actionSealDocument: string = 'sealDocument';
export const actionSealValidationFailed: string = 'sealValidationFailed';
export const actionSealDocumentFailed: string = 'sealDocumentFailed';

export const actionSetSidebarCollapsed: string = 'setSidebarCollapsed';
export const actionSaveSignatureBase64: string = 'saveSignatureBase64';
export const actionSaveSignatureBase64Failed: string = 'saveSignatureBase64Failed';

export const actionAssignFieldToRecipient: string = 'assignFieldToRecipient';
export const actionAssignFieldToRecipientFailed: string = 'assignFieldToRecipientFailed';

export const actionDeleteLoaderPage = 'deleteLoaderPage';
export const actionCreateLoaderPage = 'createLoaderPage';

export const actions: ActionTree<IDealroomState, RootState> = {
  async [actionLoadDocument]({ dispatch, commit }, documentId: string) {
    try {
      commit(mutationSetLoading, true);
      const document: IDocument = await DocumentService.loadDocument(documentId);
      dispatch(actionLoadDocumentSuccess, document);
    } catch (error) {
      dispatch(actionLoadDocumentFailed, { error, payload: { documentId } });
    }
  },
  async [actionLoadDocumentSuccess]({ commit }, document: IDocument) {
    commit(mutationSetDocument, document);
    commit(mutationSetLoading, false);
    commit(mutationSetLoaded, true);
  },
  async [actionLoadDocumentFailed]({ dispatch, commit }) {
    commit(mutationSetLoading, false);
    dispatch('alert/showDangerAlert', translate('failed-to-load-document'), {
      root: true,
    });
  },
  [actionShowEditDocumentPage](
    { commit },
    { isInEditMode, pageId }: { isInEditMode: boolean; pageId: string }
  ) {
    commit(mutationShowEditDocumentPage, { isInEditMode, pageId });
  },
  async [actionCreateDocumentPageFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-create-document-page'), {
      root: true,
    });
  },
  async [actionCreateContentLinkBlock](
    { dispatch, state },
    {
      contentLinkBlock,
      file,
      pageIndex,
    }: { contentLinkBlock: IContentLinkBlock; file: File; pageIndex: number }
  ) {
    try {
      dispatch(actionCreateLoaderPage, { pageIndex, type: EDocumentPageType.ContentLink });
      const payload: IContentLinkBlock = ContentLinkBlockService.getPayload(contentLinkBlock, file);
      const createdContentLink: IContentLinkBlock = await ContentLinkBlockService.createContentLinkBlock(
        payload
      );
      if (file) {
        const uploadLink: string = await ContentLinkBlockService.getContentLinkUploadUrl(
          createdContentLink.id
        );
        await ContentLinkBlockService.uploadThumbnail({ file, uploadLink });
      }
      dispatch(actionCreateContentLinkDocumentPage, {
        contentLinkBlock: createdContentLink,
        pageIndex,
        documentId: state.document.id,
      });
    } catch (error) {
      dispatch(actionCreateContentLinkBlockFailed, error);
    }
  },
  async [actionCreateContentLinkBlockFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-add-link'), {
      root: true,
    });
  },
  async [actionCreateContentLinkDocumentPage](
    { dispatch },
    {
      contentLinkBlock,
      pageIndex,
      documentId,
    }: { contentLinkBlock: IContentLinkBlock; pageIndex: number; documentId: string }
  ) {
    try {
      const newDocumentPage: IDocumentPage = await ContentLinkBlockService.createContentLinkDocumentPage(
        contentLinkBlock.id,
        pageIndex,
        documentId
      );
      dispatch(actionCreateDocumentPageSuccess, newDocumentPage);
      dispatch(actionDeleteLoaderPage);
    } catch (error) {
      dispatch(actionCreateDocumentPageFailed);
    }
  },
  async [actionCreateVideoDocumentPage](
    { dispatch, state },
    {
      videoId,
      pageIndex,
      loading = false,
    }: {
      videoId: string;
      pageIndex: number;
      loading?: boolean;
    }
  ) {
    try {
      const newDocumentPage: IDocumentPage = await service.createVideoDocumentPage(
        state.document.id,
        videoId,
        pageIndex
      );
      newDocumentPage.loading = loading;
      dispatch(actionCreateDocumentPageSuccess, newDocumentPage);
    } catch (error) {
      dispatch(actionCreateDocumentPageFailed);
    }
  },
  async [actionCreateEditorBlock](
    { dispatch },
    { documentId, pageIndex }: ICreateEditorBlockPayload
  ) {
    const tempBlock: IDocumentPage = EditorHelper.createTempBlock(pageIndex);

    dispatch(actionCreateDocumentPageSuccess, tempBlock);
    try {
      const newDocumentPage: IDocumentPage = await service.createEditorBlock(documentId, pageIndex);

      dispatch(actionCreateEditorBlockSuccess, {
        tempId: tempBlock.id,
        documentPage: newDocumentPage,
      });
    } catch (error) {
      dispatch(actionCreateEditorBlockFailed, tempBlock.id);
    }
  },
  [actionCreateEditorBlockSuccess]({ commit }, payload: IReplaceTempBlock) {
    commit(mutationReplaceTempBlock, payload);
  },
  [actionCreateEditorBlockFailed]({ dispatch, commit }, tempId: string) {
    commit(mutationRemoveTempBlock, tempId);
    dispatch('alert/showDangerAlert', translate('failed-to-create-document-page'), {
      root: true,
    });
  },
  async [actionCreateDocumentPageSuccess]({ dispatch, state }, newDocumentPage: IDocumentPage) {
    const updatedPages: IDocumentPage[] = DealroomHelper.addDocumentPages(
      [newDocumentPage],
      state.document.pages
    );
    dispatch(actionSetDocumentPages, updatedPages);
  },
  async [actionDeleteDocumentPage]({ dispatch, commit, state }, documentPage: IDocumentPage) {
    const originalPages: IDocumentPage[] = state.document.pages;
    try {
      const updatedPages: IDocumentPage[] = DealroomHelper.removeDocumentPage(
        documentPage.id,
        originalPages
      );
      dispatch(actionSetDocumentPages, updatedPages);
      await service.deleteDocumentPage(documentPage.id);
      if (updatedPages.length === 0) {
        dispatch(actionUpdateDocument, {
          name: '',
          externalEditorId: '',
          externalEditorType: EExternalEditorType.None,
        });
      } else {
        const deletedPageIndex: number = originalPages.findIndex(
          (page: IDocumentPage) => page.id === documentPage.id
        );
        const currentDocumentPage: IDocumentPage = DealroomHelper.getCurrentDocumentPageAfterPageDelete(
          deletedPageIndex,
          updatedPages
        );
        commit(mutationSetCurrentDocumentPage, currentDocumentPage);
      }
    } catch (error) {
      dispatch(actionDeleteDocumentPageFailed, originalPages);
    }
  },
  async [actionDeleteDocumentPageFailed]({ dispatch }, originalPages: IDocumentPage[]) {
    dispatch(actionSetDocumentPages, originalPages);
    dispatch('alert/showDangerAlert', translate('failed-to-remove-document-page'), {
      root: true,
    });
  },
  async [actionDeleteLoaderPage]({ dispatch, state }) {
    const updatedPages: IDocumentPage[] = DealroomHelper.removeLoaderPage(state.document.pages);
    dispatch(actionSetDocumentPages, updatedPages);
  },
  async [actionUpdateContentLinkPage](
    { state, dispatch, commit },
    {
      documentPage,
      file,
      fileDataUrl,
    }: { documentPage: IDocumentPage; file: File; fileDataUrl: string }
  ) {
    const linkPage: IDocumentPage = state.document.pages.find(
      (page: IDocumentPage) => page.id === documentPage.id
    );
    const { contentLinkBlock }: IDocumentPage = documentPage;
    const originalContentLinkBlock: IContentLinkBlock = !!linkPage && linkPage.contentLinkBlock;

    const thumbUrl: string = fileDataUrl || contentLinkBlock.thumbUrl;
    commit(mutationSetContentLink, {
      pageId: documentPage.id,
      contentLinkBlock: { ...contentLinkBlock, thumbUrl },
    });
    dispatch(actionShowEditDocumentPage, { isInEditMode: false, pageId: documentPage.id });

    try {
      const overwriteThumbnail: boolean =
        originalContentLinkBlock.thumbUrl !== contentLinkBlock.thumbUrl;
      const payload: IContentLinkBlock = ContentLinkBlockService.getPayload(
        contentLinkBlock,
        file,
        overwriteThumbnail
      );
      await ContentLinkBlockService.updateContentLinkBlock(payload);
      if (file) {
        const uploadLink: string = await ContentLinkBlockService.getContentLinkUploadUrl(
          contentLinkBlock.id
        );
        await ContentLinkBlockService.uploadThumbnail({ file, uploadLink });
      }
    } catch (error) {
      dispatch(actionUpdateContentLinkPageFailed, {
        pageId: documentPage.id,
        originalContentLinkBlock,
      });
    }
  },
  [actionUpdateContentLinkPageFailed](
    { dispatch, commit },
    {
      pageId,
      originalContentLinkBlock,
    }: { pageId: string; originalContentLinkBlock: IContentLinkBlock }
  ) {
    dispatch('alert/showDangerAlert', translate('failed-to-add-content-link-url'), {
      root: true,
    });
    commit(mutationSetContentLink, {
      pageId,
      contentLinkBlock: originalContentLinkBlock,
    });
  },
  async [actionUpdateVideoPage](
    { dispatch, commit, state },
    { pageId, videoId }: { pageId: string; videoId: string }
  ) {
    commit(mutationSetPageLoading, { loading: true, pageId });
    const payload: IUpdateDocumentPageInput = {
      id: pageId,
      pageType: 'video',
      videoId,
    };
    try {
      const updatedPage: IDocumentPage = await service.updateDocumentPage(payload);
      const updatedPages: IDocumentPage[] = DealroomHelper.replaceDocumentPage(
        { ...state.document.pages },
        updatedPage
      );
      commit(mutationSetDocumentPages, updatedPages);
    } catch (error) {
      console.log(error);
      dispatch(actionUpdateVideoPageFailed);
    }
    commit(mutationSetPageLoading, { loading: false, pageId });
  },
  [actionUpdateVideoPageFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('could-not-update-page'), {
      root: true,
    });
  },
  [actionCreateLoaderPage](
    { dispatch, state },
    {
      pageIndex,
      type = EDocumentPageType.DocumentContent,
    }: { pageIndex: number; type: EDocumentPageType }
  ) {
    const loaderPage: IDocumentPage = {
      id: pageIndex.toString(),
      pageType: type,
      fields: [],
      pageNum: pageIndex,
      loading: true,
    };
    const updatedPages: IDocumentPage[] = DealroomHelper.addDocumentPages(
      [loaderPage],
      state.document.pages
    );
    dispatch(actionSetDocumentPages, updatedPages);
  },
  async [actionUploadFile](
    { dispatch, state },
    { startIndex, file }: { startIndex: number; file: File }
  ) {
    try {
      await service.uploadFile(state.document.id, startIndex, file);
      dispatch(actionUploadFileSuccess, file.name);
    } catch (error) {
      dispatch(actionUploadFileFailed);
    }
  },
  async [actionUploadFileSuccess]({ dispatch, state }, filename: string) {
    if (!state.document.name) {
      dispatch(actionUpdateDocument, { name: DealroomHelper.removeExtension(filename) });
    }
    const importProgress: Partial<IProgress> = {
      currentStep: EImportStep.Uploaded,
    };
    dispatch(
      `addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`,
      importProgress
    );
  },
  async [actionUploadFileFailed]({ dispatch }) {
    dispatch(
      `addPage/importProgress/${EImportProgressAction.ImportFailed}`,
      translate('failed-to-upload-file').toString()
    );
  },
  [actionImportPages]({ dispatch, state }, pages: IDocumentPage[]) {
    const statePages: IDocumentPage[] = state.document.pages;
    const sortedPages: IDocumentPage[] = pages.sort(
      (a: IDocumentPage, b: IDocumentPage) => a.pageNum - b.pageNum
    );
    const updatedPages: IDocumentPage[] = DealroomHelper.addDocumentPages(sortedPages, statePages);
    if (!state.currentDocumentPage) {
      dispatch(actionSetCurrentDocumentPage, sortedPages[0]);
    }
    dispatch(actionSetDocumentPages, updatedPages);
  },
  async [actionResetState]({ commit }) {
    commit(mutationSetState, { ...defaultState });
  },
  async [actionShowDocumentSettings]({ commit }, show: boolean) {
    commit(mutationShowDocumentSettings, show);
  },
  async [actionUpdateDocument]({ dispatch, commit, state }, payload: IUpdateDocument) {
    const previousDocument: IDocument = state.document;
    if ([EnumDocumentType.Introduction, EnumDocumentType.Presentation].includes(payload.type)) {
      dispatch(`${fieldNamespace}/${actionDeleteSignatureFields}`, {}, { root: true });
      if (!!state.document.recipients || !!state.document.recipients.length) {
        const recipientsToUpdate: IRecipient[] = DealroomHelper.signableRecipients(
          state.document.recipients
        );
        recipientsToUpdate.forEach((recipient: IRecipient) =>
          dispatch(actionUpdateRecipient, {
            id: recipient.id,
            properties: { role: ERecipientRole.CC },
          })
        );
      }
    }
    try {
      const updatedDocument: IDocument = { ...state.document, ...payload };
      commit(mutationSetDocument, updatedDocument);
      await service.updateDocument(payload, state.document.id);
      dispatch(actionShowDocumentSettings, false);
    } catch (error) {
      dispatch(actionUpdateDocumentFailed, { previousDocument });
    }
  },
  async [actionUpdateDocumentFailed](
    { dispatch, commit },
    { previousDocument }: { previousDocument: IDocument }
  ) {
    commit(mutationSetDocument, previousDocument);
    dispatch('alert/showDangerAlert', translate('could-not-update-document'), {
      root: true,
    });
  },
  async [actionAddDocumentTag](
    { state, dispatch },
    { title, tags }: { title: string; tags: ITag[] }
  ) {
    const tagToBeAdded: ITag = tags.find((tag: ITag) => tag.title === title);
    try {
      await tagService.addDocumentTags([state.document.id], [tagToBeAdded.id]);
      dispatch(actionAddDocumentTagSuccess, tagToBeAdded);
    } catch (error) {
      dispatch(actionAddDocumentTagFailed, { title });
    }
  },
  async [actionAddDocumentTagSuccess]({ commit }, newTag: ITag) {
    commit(mutationAddDocumentTag, newTag);
  },
  async [actionAddDocumentTagFailed]({ dispatch }, { title }: { title: string }) {
    dispatch('alert/showDangerAlert', translate('Failed to add tag "{title}"', { title }), {
      root: true,
    });
  },
  async [actionRemoveDocumentTag](
    { dispatch, commit, state },
    { title, tags }: { title: string; tags: ITag[] }
  ) {
    try {
      const tagToBeRemoved: ITag = tags.find((tag: ITag) => tag.title === title);
      await tagService.removeDocumentTags([state.document.id], [tagToBeRemoved.id]);
      commit(mutationRemoveDocumentTag, tagToBeRemoved.id);
    } catch (error) {
      dispatch(actionRemoveDocumentTagFailed);
    }
  },
  async [actionRemoveDocumentTagFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('could-not-remove-tag-from-document'), {
      root: true,
    });
  },
  async [actionSetCurrentDocumentPage]({ commit }, documentPage: IDocumentPage) {
    commit(mutationSetCurrentDocumentPage, documentPage);
  },
  async [actionSetSelectedUser]({ commit }, user: IUser) {
    commit(mutationSetSelectedRecipient, { id: null, connectFirstRecipient: false });
    commit(mutationSetSelectedUser, user);
  },
  async [actionSetSelectedRecipient](
    { commit },
    {
      recipient,
      connectFirstRecipient,
    }: {
      recipient?: IRecipient;
      connectFirstRecipient: boolean;
    }
  ) {
    commit(mutationSetSelectedUser, null);
    commit(mutationSetSelectedRecipient, {
      id: recipient ? recipient.id : null,
      connectFirstRecipient,
    });
  },
  async [actionRemoveRecipient]({ state, commit, dispatch }, recipientId: string) {
    const existingRecipients: IRecipient[] = state.document.recipients;
    let remainingRecipients: IRecipient[] = existingRecipients.filter(
      (recipient: IRecipient) => recipient.id !== recipientId
    );
    if (remainingRecipients.length > 1) {
      remainingRecipients = fixRecipientsGaps(remainingRecipients);
    }
    try {
      commit(mutationSetRecipients, remainingRecipients);
      const hasSigner: boolean = state.document.recipients.some(
        (recipient: IRecipient) => recipient.role === ERecipientRole.Signer
      );

      const hasApprover: boolean = state.document.recipients.some(
        (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
      );

      if (!hasSigner && hasApprover) {
        dispatch(actionUpdateRecipientApproversToCC);
      }
      await service.deleteRecipient(recipientId);
      if (remainingRecipients.length === 1) {
        dispatch(actionTurnOffSigningOrder);
      }
      await dispatch(actionRemoveRecipientSuccess, recipientId);
    } catch (error) {
      dispatch(actionRemoveRecipientFailed, existingRecipients);
    }
  },
  async [actionRemoveRecipientSuccess]({ state, dispatch }, recipientId: string) {
    dispatch(`${fieldNamespace}/${actionLoadFields}`, state.document.id, { root: true });
    if (state.selectedRecipient.id === recipientId) {
      dispatch(actionSetSelectedRecipient, { id: null, connectFirstRecipient: false });
      dispatch(actionSetSelectedUser, state.document.user);
    }
  },
  async [actionRemoveRecipientFailed]({ commit }, oldRecipients: IRecipient[]) {
    commit(mutationSetRecipients, oldRecipients);
  },
  async [actionCreateRecipient](
    { dispatch },
    {
      contactId,
      documentId,
      role,
    }: {
      contactId: string;
      documentId: string;
      role: ERecipientRole;
    }
  ) {
    try {
      const recipient: IRecipient = await service.createRecipient(contactId, documentId, role);
      dispatch(actionCreateRecipientSuccess, recipient);
    } catch (error) {
      dispatch(actionCreateRecipientFailed, error);
    }
  },
  async [actionCreateRecipientSuccess]({ commit, dispatch, state }, recipient: IRecipient) {
    if (recipient.role === ERecipientRole.Approver) {
      dispatch(actionTurnOnSigningOrder);
    }
    if (state.document.isSigningOrder) {
      const lastRecipient: IRecipient =
        state.document.recipients &&
        state.document.recipients[state.document.recipients.length - 1];
      const orderNum: number = lastRecipient ? lastRecipient.orderNum + 1 : 1;
      commit(mutationAddRecipient, { ...recipient, orderNum });
      dispatch(actionUpdateRecipient, { id: recipient.id, properties: { orderNum } });
    } else {
      commit(mutationAddRecipient, recipient);
    }
    dispatch(actionConnectFirstRecipientToFields);
  },
  async [actionConnectFirstRecipientToFields]({ dispatch, rootState, getters }) {
    const firstRecipient: IRecipient = getters[getterFirstAddedRecipient];

    const fieldsWithMissingRecipient: IField[] = rootState[fieldNamespace].fields.filter(
      (field: IField) => field.source === EFieldSource.Recipient && !field.recipientId
    );

    if (firstRecipient && !!fieldsWithMissingRecipient.length) {
      const updatedFields: IField[] = fieldsWithMissingRecipient.map((field: IField) => {
        return {
          ...field,
          recipientId: firstRecipient.id,
        };
      });

      if (getters[getterConnectFirstRecipient]) {
        dispatch(actionSetSelectedRecipient, {
          recipient: firstRecipient,
          connectFirstRecipient: false,
        });
      }
      dispatch(`${fieldNamespace}/${actionUpdateFields}`, updatedFields, { root: true });
    }
  },
  async [actionCreateRecipientFailed]() {},
  async [actionTurnOnSigningOrder]({ dispatch, state }) {
    dispatch(actionUpdateDocument, { ...state.document, isSigningOrder: 1 });
  },
  async [actionTurnOffSigningOrder]({ dispatch, state }) {
    dispatch(actionUpdateDocument, { ...state.document, isSigningOrder: 0 });
  },
  async [actionUpdateRecipient](
    { commit, dispatch, state },
    { id, properties }: { id: string; properties: Partial<IRecipient> }
  ) {
    const oldRecipient: IRecipient = state.document.recipients.find(
      (recipient: IRecipient) => recipient.id === id
    );
    const updatedRecipient: IRecipient = { ...oldRecipient, ...properties };
    commit(mutationUpdateRecipient, updatedRecipient);

    const hasSigner: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Signer
    );

    const hasApprover: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    if (!hasSigner && hasApprover) {
      dispatch(actionUpdateRecipientApproversToCC);
    }
    try {
      await service.updateRecipient(id, properties);
      if (updatedRecipient.role === ERecipientRole.Approver) {
        await dispatch(actionTurnOnSigningOrder);
      }
      if (
        oldRecipient.role !== ERecipientRole.Approver &&
        updatedRecipient.role === ERecipientRole.Approver &&
        updatedRecipient.orderNum !== 1
      ) {
        dispatch(actionUpdateRecipientsSigningOrder, {
          oldIndex: updatedRecipient.orderNum - 1,
          newIndex: 0,
          drag: true,
        });
      }
    } catch (error) {
      dispatch(actionUpdateRecipientFailed, oldRecipient);
    }
  },
  async [actionUpdateRecipientApproversToCC]({ dispatch, state, commit }) {
    const approverRecipients: IRecipient[] = state.document.recipients.filter(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    approverRecipients.forEach(async (oldRecipient: IRecipient) => {
      const updatedRecipient: IRecipient = { ...oldRecipient, role: ERecipientRole.CC };
      commit(mutationUpdateRecipient, updatedRecipient);
      try {
        await service.updateRecipient(oldRecipient.id, { role: ERecipientRole.CC });
      } catch {
        dispatch(actionUpdateRecipientFailed, oldRecipient);
      }
    });
  },
  async [actionUpdateRecipientFailed]({ commit }, oldRecipient: IRecipient) {
    commit(mutationUpdateRecipient, oldRecipient);
  },
  async [actionDeleteAttachment](
    { commit, dispatch, state },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.filter(
        (currentAttachment: IDocumentAttachment) => currentAttachment.id !== documentAttachment.id
      );
      commit(mutationUpdateDocumentAttachment, updatedDocumentAttachments);

      const response: boolean = await service.deleteAttachment(documentAttachment);
      if (!response) {
        dispatch(actionDeleteAttachmentFailed, originalDocumentAttachments);
      }
    } catch (e) {
      dispatch(actionDeleteAttachmentFailed, originalDocumentAttachments);
    }
  },

  async [actionDeleteAttachmentFailed](
    { dispatch, commit },
    originalDocumentAttachments: IDocumentAttachment[]
  ) {
    commit(mutationUpdateDocumentAttachment, originalDocumentAttachments);
    dispatch('alert/showDangerAlert', translate('could-not-delete-attachment'), {
      root: true,
    });
  },
  async [actionRestrictAccessAttachment](
    { dispatch, state, commit },
    {
      documentAttachment,
      recipientIds,
    }: {
      documentAttachment: IDocumentAttachment;
      recipientIds: string[];
    }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      if (recipientIds.length > 0) {
        const updatedDocumentAttachments = originalDocumentAttachments.map(
          (currentAttachment: IDocumentAttachment) => {
            if (currentAttachment.id === documentAttachment.id) {
              currentAttachment.accessRecipientList = state.document.recipients.filter(
                (recipient: IRecipient) => recipientIds.indexOf(recipient.id) > -1
              );
              currentAttachment.restrictAccess = true;
            }
            return currentAttachment;
          }
        );
        commit(mutationUpdateDocumentAttachment, updatedDocumentAttachments);

        const response: boolean = await service.attachmentRestrictAccess(
          documentAttachment,
          recipientIds
        );
        if (!response) {
          dispatch(actionRestrictAccessAttachmentFailed, originalDocumentAttachments);
        }
      } else {
        dispatch(actionRemoveRestrictedAccessAttachment, {
          documentAttachment,
        });
      }
    } catch (e) {
      dispatch(actionRestrictAccessAttachmentFailed, originalDocumentAttachments);
    }
  },
  async [actionRestrictAccessAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(mutationUpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      'alert/showDangerAlert',
      translate('could-not-update-restricted-access-for-attachment'),
      {
        root: true,
      }
    );
  },
  async [actionRemoveRestrictedAccessAttachment](
    { dispatch, state, commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.map(
        (currentAttachment: IDocumentAttachment) => {
          if (currentAttachment.id === documentAttachment.id) {
            currentAttachment.restrictAccess = false;
            currentAttachment.accessRecipientList = [];
          }
          return currentAttachment;
        }
      );
      commit(mutationUpdateDocumentAttachment, updatedDocumentAttachments);
      const response: boolean = await service.attachmentRemoveRestrictedAccess(documentAttachment);
      if (!response) {
        dispatch(actionRemoveRestrictedAccessAttachmentFailed, 'error');
      }
    } catch (e) {
      dispatch(actionRemoveRestrictedAccessAttachmentFailed, e);
    }
  },
  async [actionRemoveRestrictedAccessAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(mutationUpdateDocumentAttachment, originalDocumentAttachments);
    dispatch(
      'alert/showDangerAlert',
      translate('could-not-remove-restricted-access-from-attachment'),
      {
        root: true,
      }
    );
  },
  async [actionRequireViewAttachment](
    { dispatch, state, commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment }
  ) {
    const originalDocumentAttachments: IDocumentAttachment[] = state.document.documentAttachments;
    try {
      const updatedDocumentAttachments = originalDocumentAttachments.map(
        (currentAttachment: IDocumentAttachment) => {
          if (currentAttachment.id === documentAttachment.id) {
            currentAttachment.requireView = !currentAttachment.requireView;
          }
          return currentAttachment;
        }
      );
      commit(mutationUpdateDocumentAttachment, updatedDocumentAttachments);
      const response: boolean = await service.attachmentRequireView(documentAttachment);
      if (!response) {
        dispatch(actionRequireViewAttachmentFailed, originalDocumentAttachments);
      }
    } catch (e) {
      dispatch(actionRequireViewAttachmentFailed, originalDocumentAttachments);
    }
  },
  async [actionRequireViewAttachmentFailed](
    { dispatch, commit },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(mutationUpdateDocumentAttachment, originalDocumentAttachments);
    dispatch('alert/showDangerAlert', translate('could-not-update-attachment'), {
      root: true,
    });
  },
  async [actionLoadDocumentAttachments](
    { dispatch, state },
    { documentId }: { documentId: string }
  ) {
    const originalDocumentAttachments = state.document.documentAttachments;
    try {
      const documentAttachment: IDocumentAttachment[] = await service.loadDocumentAttachments(
        documentId
      );
      dispatch(actionLoadDocumentAttachmentsSuccess, { documentAttachment });
    } catch (e) {
      dispatch(actionLoadDocumentAttachmentsFailed, originalDocumentAttachments);
    }
  },
  async [actionLoadDocumentAttachmentsSuccess](
    { commit },
    { documentAttachment }: { documentAttachment: IDocumentAttachment[] }
  ) {
    commit(mutationUpdateDocumentAttachment, documentAttachment);
  },
  async [actionLoadDocumentAttachmentsFailed](
    { commit, dispatch },
    { originalDocumentAttachments }: { originalDocumentAttachments: IDocumentAttachment[] }
  ) {
    commit(mutationUpdateDocumentAttachment, originalDocumentAttachments);
    dispatch('alert/showDangerAlert', translate('could-not-load-attachments'), {
      root: true,
    });
  },
  async [actionUpdateRecipientsSigningOrder](
    { commit, dispatch, state },
    { newIndex, oldIndex, drag }: { newIndex: number; oldIndex: number; drag: boolean }
  ) {
    const oldRecipients: IRecipient[] = state.document.recipients;
    let sortedRecipients: IRecipient[] = arrangeRecipientSignOrder(
      newIndex,
      oldIndex,
      oldRecipients,
      drag
    );

    const hasApprover: boolean = state.document.recipients.some(
      (recipient: IRecipient) => recipient.role === ERecipientRole.Approver
    );

    if (hasApprover) {
      const lastApprover: IRecipient = [...sortedRecipients]
        .reverse()
        .find((recipient: IRecipient) => recipient.role === ERecipientRole.Approver);
      const lastSigner: IRecipient = [...sortedRecipients]
        .reverse()
        .find((recipient: IRecipient) => recipient.role === ERecipientRole.Signer);

      if (lastApprover.orderNum >= lastSigner.orderNum) {
        dispatch('alert/showWarningAlert', translate('approver-cant-be-last-in-order'), {
          root: true,
        });
        const lastSignerIndex: number = sortedRecipients.findIndex(
          recipient => recipient.id === lastSigner.id
        );
        sortedRecipients = arrangeRecipientSignOrder(
          lastApprover.orderNum,
          lastSignerIndex,
          sortedRecipients,
          drag
        );
      }
    }

    const recipientOrder: Partial<IRecipient>[] = sortedRecipients.map((recipient: IRecipient) => ({
      id: recipient.id,
      orderNum: recipient.orderNum,
    }));

    commit(mutationSetRecipients, sortedRecipients);
    try {
      await service.updateRecipientOrder(state.document.id, recipientOrder);
    } catch (error) {
      dispatch(actionUpdateRecipientsSigningOrderFailed, oldRecipients);
    }
  },
  async [actionUpdateRecipientsSigningOrderFailed]({ commit }, oldRecipients: IRecipient[]) {
    commit(mutationSetRecipients, oldRecipients);
  },
  async [actionLoadRecipients]({ dispatch, state }) {
    try {
      const recipients: IRecipient[] = await service.loadRecipients(state.document.id);
      dispatch(actionLoadRecipientsSuccess, recipients);
    } catch {
      dispatch(actionLoadRecipientsFailed);
    }
  },
  async [actionLoadRecipientsSuccess]({ commit, dispatch }, recipients: IRecipient[]) {
    commit(mutationSetRecipients, recipients);

    if (recipients.length === 1) {
      dispatch(actionConnectFirstRecipientToFields);
    }
  },
  async [actionLoadRecipientsFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-load-recipients'), {
      root: true,
    });
  },
  async [actionUseTemplate](
    { state, dispatch, commit },
    {
      templateId,
      pageOffset,
      name,
      externalEditorType,
    }: {
      templateId: string;
      pageOffset: number;
      name: string;
      externalEditorType?: EExternalEditorType;
    }
  ) {
    let type: EImportType = EImportType.Template;
    if (externalEditorType && externalEditorType === EExternalEditorType.Docs) {
      commit(mutationSetDocument, { ...state.document, externalEditorType });
      type = EImportType.GoogleDocs;
    }
    dispatch(`addPage/importProgress/${EImportProgressAction.InitializeImport}`, {
      name,
      type,
      index: pageOffset - 1,
    });
    try {
      const document = await service.useTemplate(state.document.id, templateId, pageOffset);
      if (externalEditorType !== EExternalEditorType.Docs) {
        dispatch(actionUseTemplateSuccess, document);
      } else {
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Uploaded,
        });
      }
    } catch {
      dispatch(actionUseTemplateFailed);
    }
  },
  async [actionUseTemplateSuccess]({ commit, dispatch, state }, document: IDocument) {
    await dispatch(`addPage/importProgress/${EImportProgressAction.CompleteTemplateImport}`);
    await dispatch(`${fieldNamespace}/${actionLoadFields}`, document.id, { root: true });
    commit(mutationSetDocument, document);
    if (!state.currentDocumentPage) {
      dispatch(
        actionSetCurrentDocumentPage,
        document.pages.find(
          (page: IDocumentPage) => page.pageType === EDocumentPageType.DocumentContent
        )
      );
    }
    dispatch(actionConnectFirstRecipientToFields);
    dispatch(actionLoadLegacyDocumentData);
  },
  async [actionUseTemplateFailed]({ dispatch }) {
    dispatch(
      `addPage/importProgress/${EImportProgressAction.ImportFailed}`,
      translate('failed-to-use-template').toString()
    );
  },
  async [actionLoadLegacyDocumentData]({ state, dispatch }) {
    dispatch(`${documentEditNamespace}/${actionLoadDocumentData}`, state.document.id, {
      root: true,
    });
    dispatch(`${documentEditNamespace}/${actionInitializeDocumentEdit}`, null, {
      root: true,
    });
  },
  async [actionDeleteDocuments]({ dispatch }, ids: [string]) {
    try {
      await service.deleteDocuments(ids);
      dispatch(actionDeleteDocumentsSuccess);
    } catch {
      dispatch(actionDeleteDocumentsFailed);
    }
  },
  async [actionDeleteDocumentsSuccess]({ dispatch }) {
    dispatch('alert/showSuccessAlert', translate('document-successfully-deleted'), {
      root: true,
    });
    dispatch(actionResetState);
  },
  async [actionDeleteDocumentsFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-delete-document'), {
      root: true,
    });
    dispatch(actionResetState);
  },
  async [actionArrangeDocumentPages]({ dispatch, state }, pages: IDocumentPage[]) {
    const oldPages: IDocumentPage[] = state.document.pages;
    const documentPageIds: string[] = pages.map((page: IDocumentPage) => page.id);
    const pageNums: number[] = pages.map((page: IDocumentPage) => page.pageNum);

    try {
      dispatch(actionSetDocumentPages, pages);
      await service.sortDocumentPages(state.document.id, documentPageIds, pageNums);
    } catch (error) {
      dispatch(actionArrangeDocumentPagesFailed, oldPages);
    }
  },
  async [actionArrangeDocumentPagesFailed]({ dispatch }, pages: IDocumentPage[]) {
    dispatch(actionSetDocumentPages, pages);
    dispatch('alert/showDangerAlert', translate('failed-to-move-page'), {
      root: true,
    });
  },
  async [actionSaveGoogleDrive](
    { state, dispatch },
    { fileUrl, fileName, pageIndex }: { fileUrl: string; fileName: string; pageIndex: number }
  ) {
    dispatch(`addPage/importProgress/${EImportProgressAction.InitializeImport}`, {
      fileName,
      index: pageIndex - 1,
      type: EImportType.GoogleDrive,
    });
    try {
      const data: IUploadData = await GoogleService.upload(fileName, fileUrl);
      if (!data.success) {
        dispatch(actionSaveGoogleDriveFailed);
      } else {
        await GoogleService.save(data.filename, state.document.id, pageIndex);
        await GoogleService.saveImportLog(state.document.id);
        if (!state.document.name) {
          dispatch(actionUpdateDocument, { name: fileName });
        }
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Uploaded,
        });
      }
    } catch (error) {
      dispatch(actionSaveGoogleDriveFailed);
    }
  },
  async [actionSaveGoogleDriveFailed]({ dispatch }) {
    dispatch(actionHandleGoogleDriveError, EGoogleDriveError.Import);
  },
  async [actionInitializePageImport]({ dispatch }, payload: IDocumentImportData) {
    const progress: Partial<IProgress> = {
      currentStep: EImportStep.PreparingImport,
      totalPageCount: payload.totalCount,
    };
    dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, progress);
  },
  async [actionLoadDocumentPage]({ dispatch, state }, documentPageId: string) {
    const documentId: string = state.document && state.document.id;
    if (!documentId) {
      return;
    }

    const { progress } = state['addPage'].importProgress as IImportProgressState;
    if (progress.pages.length === 0) {
      dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
        currentStep: EImportStep.ImportingPages,
      });
    }
    try {
      const page: IDocumentPage = await DocumentService.loadDocumentPage(
        documentId,
        documentPageId
      );
      await dispatch(actionLoadDocumentPageSuccess, page);
    } catch (error) {
      dispatch(actionLoadDocumentPageFailed);
    }
  },
  async [actionLoadDocumentPageSuccess]({ state, dispatch }, documentPage: IDocumentPage) {
    const importProgressState: IImportProgressState = state['addPage']
      .importProgress as IImportProgressState;
    if (importProgressState.progress.totalPageCount === 0) {
      dispatch(actionReplaceDocumentPage);
    } else {
      const importedPages: IDocumentPage[] = importProgressState.progress.pages;
      const newPages: IDocumentPage[] = [...importedPages, documentPage];
      const importProgress: Partial<IProgress> = { pages: newPages };
      dispatch(
        `addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`,
        importProgress
      );
      const allLoaded: boolean =
        importProgressState.progress.pages.length === importProgressState.progress.totalPageCount;
      if (allLoaded) {
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Done,
        });
        const { externalEditorId, externalEditorType } = state.document;
        if (externalEditorType && !externalEditorId) {
          await dispatch(actionLoadDocument, state.document.id);
        }
        // Reloads legacy document data. Used in legacy engage preview
        window['reloadDocument']();
      }
    }
  },
  async [actionLoadDocumentPageFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-fetch-document-page'), {
      root: true,
    });
  },
  [actionReplaceDocumentPage]({ commit, dispatch, state }, documentPage: IDocumentPage) {
    if (!state.document) {
      commit(mutationSetDocument, { pages: [documentPage] });
    } else {
      const pages: IDocumentPage[] = DealroomHelper.replaceDocumentPage(
        state.document.pages,
        documentPage
      );
      dispatch(actionSetDocumentPages, pages);
    }
  },
  async [actionHandleGoogleDriveError](
    { dispatch },
    type: EGoogleDriveError = EGoogleDriveError.Unknown
  ) {
    let message: string;
    switch (type) {
      case EGoogleDriveError.Authenticate:
        message = translate('failed-to-authenticate');
        break;
      case EGoogleDriveError.Fetch:
        message = translate('failed-to-fetch-data-from-google-drive');
        break;
      case EGoogleDriveError.Import:
        message = translate('failed-to-import-google-drive-document');
        break;
      default:
        message = translate('error-with-google-drive-integration');
    }
    dispatch('alert/showDangerAlert', message, { root: true });
  },
  async [actionUpdateDocumentUser](
    { dispatch, commit, state },
    { documentId, user }: { documentId: string; user: IUser }
  ) {
    try {
      await service.updateDocument({ userId: user.id }, documentId);
      dispatch(`${fieldNamespace}/${actionLoadSenderSignature}`, user.id, { root: true });
      commit(mutationUpdateDocumentUser, user);
      if (state.selectedUser) {
        dispatch(actionSetSelectedUser, user);
      }
    } catch {
      dispatch(actionUpdateDocumentUserFailed);
    }
  },
  async [actionUpdateDocumentUserFailed]({ dispatch, commit }) {
    commit(mutationSetLoading, false);
    dispatch('alert/showDangerAlert', translate('failed-to-change-document-owner'), {
      root: true,
    });
  },
  async [actionSaveGoogleDocs]({ dispatch }) {
    dispatch(actionSetDocumentPages, []);
    dispatch(actionCreateLoaderPage, { pageIndex: 0 });
  },
  async [actionSortSigningRecipients]({ dispatch, state }) {
    state.document.recipients
      .filter((recipient: IRecipient) => recipient.role == 'signer')
      .map((recipient: IRecipient, idx: number) => {
        dispatch(actionUpdateRecipient, { id: recipient.id, properties: { orderNum: idx + 1 } });
      });
  },
  async [actionUploadWordFile](
    { dispatch, state },
    { file, pageNum }: { file: File; pageNum: number }
  ) {
    dispatch(`addPage/importProgress/${EImportProgressAction.InitializeImport}`, {
      name: file.name,
      index: pageNum - 1,
      type: EImportType.WordMerge,
    });
    if (state.document && !state.document.name) {
      dispatch(actionUpdateDocument, {
        name: DealroomHelper.removeExtension(file.name) || 'wordmerge',
      });
    }
    try {
      const response: IWordUploadResponse = await WordService.upload(file);
      dispatch(actionProcessWordFile, {
        filename: response.filename,
        missing: [],
        pageNum,
      });
    } catch (e) {
      dispatch(
        `addPage/importProgress/${EImportProgressAction.ImportFailed}`,
        translate('failed-to-upload-word-file').toString()
      );
    }
  },
  async [actionProcessWordFile](
    { dispatch, state },
    {
      filename,
      missing,
      pageNum,
    }: { filename: string; missing: IWordMergeField[]; pageNum: number }
  ) {
    dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
      currentStep: EImportStep.Processing,
    });
    dispatch(actionSetWordMerge, defaultState.wordMerge);
    try {
      const response: IProcessWordResponse = await WordService.process(
        state.document.id,
        filename,
        missing,
        pageNum
      );
      dispatch(actionSetWordMerge, {
        filename: response.filename,
        missing: response.missing || [],
        index: pageNum,
      });
      if (response.status === 1) {
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Processed,
        });
        await WordService.saveImportLog(state.document.id);
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Uploaded,
        });
      }
    } catch (e) {
      dispatch(
        `addPage/importProgress/${EImportProgressAction.ImportFailed}`,
        translate('failed-to-process-word-file')
      );
    }
  },
  async [actionSaveWordMergeTemplate]({ state, dispatch }, { documentId, fileName, index }: any) {
    try {
      dispatch(actionSetWordMerge, { missing: [] });
      const response: IWordTemplateResponse = await WordService.saveWordTemplate(
        documentId,
        fileName,
        index
      );
      if (response.status === 1) {
        const loaderPages: IDocumentPage[] = state.document.pages.filter(
          (page: IDocumentPage) => page.loading
        );
        dispatch(actionSetDocumentPages, loaderPages);
        dispatch(actionUpdateDocument, { externalEditorType: response.externalEditorType });
        dispatch(actionSetWordMerge, defaultState.wordMerge);
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Processed,
        });
        await WordService.saveImportLog(state.document.id);
        dispatch(`addPage/importProgress/${EImportProgressAction.UpdateImportProgress}`, {
          currentStep: EImportStep.Uploaded,
        });
      }
    } catch (e) {
      dispatch(
        `addPage/importProgress/${EImportProgressAction.ImportFailed}`,
        translate('failed-to-import-word-template').toString()
      );
      dispatch(actionSetWordMerge, defaultState.wordMerge);
    }
  },
  [actionSetWordMerge]({ commit, state }, payload: Partial<IWordMerge>) {
    commit(mutationSetWordMerge, { ...state.wordMerge, ...payload });
  },
  [actionCancelWordMerge]({ dispatch }) {
    dispatch(actionSetWordMerge, defaultState.wordMerge);
    dispatch(actionDeleteLoaderPage);
  },
  async [actionGetPreviewToken]({ commit, dispatch, state }) {
    try {
      const previewToken = await DocumentService.getPreviewToken(state.document.id);
      commit(mutationSetPreviewToken, previewToken);
    } catch {
      dispatch('alert/showDangerAlert', translate('failed-to-preview-document'), {
        root: true,
      });
    }
  },
  async [actionUpdateRecipientMobile](
    { dispatch, state },
    { id, mobile }: { id: string; mobile: string }
  ) {
    const oldRecipient: IRecipient = state.document.recipients.find(
      (recipient: IRecipient) => recipient.id === id
    );
    const updatedRecipient: IRecipient = { ...oldRecipient, ...{ mobile } };
    try {
      await service.updateRecipient(id, { mobile });
      dispatch(actionUpdateRecipientMobileSuccess, updatedRecipient);
    } catch {
      dispatch(actionUpdateRecipientFailed, oldRecipient);
    }
  },
  [actionUpdateRecipientMobileSuccess]({ commit }, recipient: IRecipient) {
    commit(mutationUpdateRecipient, recipient);
  },
  [actionSetDocumentPages]({ commit }, pages: IDocumentPage[]) {
    const uniquePages: IDocumentPage[] = pages.reduce(
      (acc: IDocumentPage[], curr: IDocumentPage) => {
        const exists: boolean = !!acc.find(page => page.id === curr.id);
        return exists ? acc : [...acc, curr];
      },
      []
    );
    commit(mutationSetDocumentPages, uniquePages);
  },
  async [actionloadPageThumbUrl]({ commit, state }, pageId: string) {
    const { thumbUrl }: Partial<IDocumentPage> = await DocumentService.loadPageThumbUrl(
      pageId,
      state.document.id
    );
    const pages: IDocumentPage[] = state.document.pages.map((page: IDocumentPage) =>
      page.id === pageId
        ? {
            ...page,
            thumbUrl,
          }
        : page
    );
    commit(mutationSetDocumentPages, pages);
  },
  async [actionCreateDocument]({ dispatch }, documentType: string) {
    try {
      dispatch('setContentIsLoading', true, { root: true });
      const body = new FormData();
      body.append('action', 'data');
      body.append('document-type', documentType);

      const documentId: string = await service.createDocument(body);
      dispatch(actionCreateDocumentSuccess, documentId);
    } catch (error) {
      dispatch(actionCreateDocumentFailed, error);
    }
  },
  async [actionCreateDocumentSuccess]({ commit, dispatch }, documentId: string) {
    commit(mutationSetCreateDocumentId, documentId);
    dispatch('setContentIsLoading', false, { root: true });
  },

  async [actionCreateDocumentFailed]({ dispatch }, error: any) {
    console.error(`Error while creating new document ${error}`);
    dispatch('alert/showDangerAlert', translate('failed-to-create-document'), {
      root: true,
    });
    dispatch('setContentIsLoading', false, { root: true });
  },

  async [actionSealDocument](
    { commit, dispatch },
    { documentId, sendDocument }: { documentId: string; sendDocument: boolean }
  ) {
    try {
      await service.sealDocument(documentId, sendDocument);
      commit(mutationSetSealValidationSuccess, true);
    } catch (error) {
      dispatch(actionSealValidationFailed);
    }
  },
  async [actionSealValidationFailed]({ commit, dispatch }) {
    commit(mutationSetSealValidationSuccess, false);
    dispatch('alert/showDangerAlert', translate('failed-to-validate-document'), {
      root: true,
    });
  },
  async [actionSealDocumentFailed]({ commit, dispatch }) {
    commit(mutationSetSealValidationSuccess, null);
    dispatch('alert/showDangerAlert', translate('failed-to-seal-document'), {
      root: true,
    });
  },
  async [actionDeleteTemplate]({ dispatch }, id: string) {
    try {
      await service.deleteTemplate(id);
    } catch (error) {
      dispatch(actionDeleteTemplateFailed);
    }
  },
  async [actionDeleteTemplateFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-delete-template'), {
      root: true,
    });
  },
  async [actionSetSidebarCollapsed]({ commit }, sidebarCollapsed: boolean) {
    commit(mutationSetSidebarCollapsed, sidebarCollapsed);
  },
  async [actionSaveSignatureBase64]({ dispatch }, signature: string[]) {
    const mappedSignature: string = `data:${signature.join(',')}`;
    try {
      await service.updateSignature(mappedSignature);
    } catch (error) {
      dispatch(actionSaveSignatureBase64Failed, error);
    }
  },
  async [actionSaveSignatureBase64Failed](_, error: any) {
    console.error('Could not save signature in base64', error);
  },
  async [actionAssignFieldToRecipient]({ dispatch, state }, recipientId: string) {
    try {
      await service.assignFieldToRecipient(state.document.id, recipientId);
    } catch {
      dispatch(actionAssignFieldToRecipientFailed);
    }
  },
  [actionAssignFieldToRecipientFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-connect-fields-to-recipient'), {
      root: true,
    });
  },
};
